'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function EnIyiDudakDolgusuMarkasi() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/en-iyi-dudak-dolgusu-markasi-dogallik-kalicilik-ve-guven';
  const pageTitle = 'En İyi Dudak Dolgusu Markası: Doğallık, Kalıcılık ve Güven';
  const pageDescription = 'En iyi dudak dolgusu markası hangisidir? Kalıcılığı, doğallığı, FDA onayı ve güvenilirliğiyle öne çıkan dudak dolgularının karşılaştırması.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-pink-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'En İyi Dudak Dolgusu Markası', href: '/tr/blog/en-iyi-dudak-dolgusu-markasi-dogallik-kalicilik-ve-guven' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="En iyi dudak dolgusu markaları karşılaştırması"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-rose-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Dudak Dolgusu & Markalar
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              En İyi Dudak Dolgusu Markası: Doğallık, Kalıcılık ve Güven
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>24 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>8 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Dudak dolgusu, yüz ifadesini yumuşatmanın ve doğal güzelliği ön plana çıkarmanın en zarif yollarından biridir.
                Ancak dolgu sonucunun estetik ve doğal görünmesi yalnızca uygulayıcı hekimin deneyimine değil, kullanılan ürünün kalitesine de doğrudan bağlıdır.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Her dolgu markasının yoğunluğu, kalıcılığı ve doku uyumu farklıdır.
                Bu yazıda, en iyi dudak dolgusu markalarını, klinik güvenlik kriterleriyle birlikte inceleyeceğiz.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Dudak Dolgusu Seçerken Nelere Dikkat Edilmeli?
              </h2>
              
              <div className="bg-rose-50 rounded-xl p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <span className="text-rose-600 mr-2">✅</span>
                      <span className="text-rose-800">FDA ve CE onaylı olmalı</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-rose-600 mr-2">✅</span>
                      <span className="text-rose-800">Yüz bölgesi için özel olarak geliştirilmiş formda olmalı</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-rose-600 mr-2">✅</span>
                      <span className="text-rose-800">Hyaluronik asit (HA) bazlı olmalı</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <span className="text-rose-600 mr-2">✅</span>
                      <span className="text-rose-800">Doğal hareketi koruyacak esneklikte</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-rose-600 mr-2">✅</span>
                      <span className="text-rose-800">İstenirse tamamen geri alınabilir (hyaluronidaz ile)</span>
                    </div>
                  </div>
                </div>
                <div className="bg-rose-100 rounded-lg p-4 mt-4">
                  <p className="text-rose-900 font-medium">
                    💬 Doğru ürün + doğru hekim = doğal dudak ifadesi.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. En Güvenilir Dudak Dolgusu Markaları (2026 Güncel Liste)
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-rose-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Marka</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Menşei</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Özellik</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Kalıcılık</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Avantaj</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Juvederm Volbella / Volift</td>
                      <td className="border border-gray-300 px-4 py-3">ABD (Allergan)</td>
                      <td className="border border-gray-300 px-4 py-3">Yumuşak, esnek yapı</td>
                      <td className="border border-gray-300 px-4 py-3">9–12 ay</td>
                      <td className="border border-gray-300 px-4 py-3">FDA onaylı, en doğal sonuç</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Restylane Kysse</td>
                      <td className="border border-gray-300 px-4 py-3">İsveç (Galderma)</td>
                      <td className="border border-gray-300 px-4 py-3">Orta yoğunluk, uzun süreli</td>
                      <td className="border border-gray-300 px-4 py-3">9–12 ay</td>
                      <td className="border border-gray-300 px-4 py-3">Gülme hareketinde esnek</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Teosyal Kiss</td>
                      <td className="border border-gray-300 px-4 py-3">İsviçre (Teoxane)</td>
                      <td className="border border-gray-300 px-4 py-3">Nemlendirici HA içeriği</td>
                      <td className="border border-gray-300 px-4 py-3">8–10 ay</td>
                      <td className="border border-gray-300 px-4 py-3">Dudak hattını belirginleştirir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Belotero Intense</td>
                      <td className="border border-gray-300 px-4 py-3">Almanya (Merz)</td>
                      <td className="border border-gray-300 px-4 py-3">Yüksek yoğunluk, kalıcı</td>
                      <td className="border border-gray-300 px-4 py-3">10–14 ay</td>
                      <td className="border border-gray-300 px-4 py-3">Dolgun etki isteyenlere</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Neauvia Intense Lips</td>
                      <td className="border border-gray-300 px-4 py-3">Polonya</td>
                      <td className="border border-gray-300 px-4 py-3">PEG çapraz bağlı HA</td>
                      <td className="border border-gray-300 px-4 py-3">10–12 ay</td>
                      <td className="border border-gray-300 px-4 py-3">Hidrofilik yapı, parlak doku</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Stylage Special Lips</td>
                      <td className="border border-gray-300 px-4 py-3">Fransa</td>
                      <td className="border border-gray-300 px-4 py-3">Antioksidan içerikli</td>
                      <td className="border border-gray-300 px-4 py-3">8–10 ay</td>
                      <td className="border border-gray-300 px-4 py-3">Şişlik riski az, hassas dudaklar için</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Kalıcılığı Etkileyen Faktörler
              </h2>
              
              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-blue-800">
                  <li>Metabolizma hızı</li>
                  <li>Kullanılan dolgu miktarı</li>
                  <li>Dudak hareketliliği</li>
                  <li>Dolgunun çapraz bağ yoğunluğu</li>
                  <li>İlk 48 saatteki bakım alışkanlıkları</li>
                </ul>
                <div className="bg-blue-100 rounded-lg p-4 mt-4">
                  <p className="text-blue-900 font-medium">
                    Ortalama kalıcılık süresi: 9–12 ay.
                  </p>
                  <p className="text-blue-900">
                    Düzenli yaptıranlarda 2. seanstan sonra kalıcılık 15 aya kadar uzar.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. En Doğal Sonucu Veren Markalar
              </h2>
              
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <p className="text-green-800 mb-4 font-medium">
                  Doğallık açısından en başarılı markalar:
                </p>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <span className="text-green-600 mr-3 text-xl">💎</span>
                    <div>
                      <span className="text-green-800 font-medium">Juvederm Volbella</span>
                      <span className="text-green-700"> → İnce dudaklarda "rötuş" etkisi</span>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-600 mr-3 text-xl">💎</span>
                    <div>
                      <span className="text-green-800 font-medium">Restylane Kysse</span>
                      <span className="text-green-700"> → Gülme sırasında esnek yapı</span>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-600 mr-3 text-xl">💎</span>
                    <div>
                      <span className="text-green-800 font-medium">Teosyal Kiss</span>
                      <span className="text-green-700"> → Nem takviyesi ve ışıltı etkisi</span>
                    </div>
                  </div>
                </div>
                <div className="bg-green-100 rounded-lg p-4 mt-4">
                  <p className="text-green-900 font-medium">
                    💬 Doğallık, dudakların konuştuğunda değil, sustuğunda bile güzel görünmesidir.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Güvenlik ve Orijinallik
              </h2>
              
              <div className="bg-orange-50 rounded-xl p-6 mb-6">
                <p className="text-orange-800 mb-4 font-medium">
                  Orijinal ürünlerin üzerinde:
                </p>
                <ul className="list-disc list-inside space-y-2 text-orange-800">
                  <li>Barkod ve LOT numarası,</li>
                  <li>Türkçe ithalat etiketi,</li>
                  <li>CE/FDA logosu,</li>
                  <li>Hologram güvenlik bandı bulunmalıdır.</li>
                </ul>
                <div className="bg-orange-100 rounded-lg p-4 mt-4">
                  <p className="text-orange-900 font-medium">
                    Kutu hasta önünde açılmalı ve etiket hasta dosyasına yapıştırılmalıdır.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Fiyat Karşılaştırması
              </h2>
              
              <div className="bg-purple-50 rounded-xl p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-purple-800 font-medium">Juvederm Volbella</span>
                      <span className="text-purple-900 font-bold">₺3.500 - ₺4.500</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-purple-800 font-medium">Restylane Kysse</span>
                      <span className="text-purple-900 font-bold">₺3.200 - ₺4.200</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-purple-800 font-medium">Teosyal Kiss</span>
                      <span className="text-purple-900 font-bold">₺2.800 - ₺3.800</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-purple-800 font-medium">Belotero Intense</span>
                      <span className="text-purple-900 font-bold">₺3.800 - ₺4.800</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-purple-800 font-medium">Neauvia Intense Lips</span>
                      <span className="text-purple-900 font-bold">₺2.500 - ₺3.500</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-purple-800 font-medium">Stylage Special Lips</span>
                      <span className="text-purple-900 font-bold">₺2.200 - ₺3.200</span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <p className="text-purple-800 text-sm">
                    * Fiyatlar 2026 yılı için geçerlidir. Detaylı fiyat bilgisi için iletişime geçin.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Uzman Görüşü
              </h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <p className="text-blue-800 italic">
                  "Dudak dolgusunda markadan çok, ürünün kişiye uygunluğu önemlidir.
                  Bazı dudaklar ince yapılıdır ve yumuşak dolgular tercih edilir; bazıları daha hacimlidir, yoğun formlar gerekir.
                  En iyi dudak dolgusu, sizin yüz oranınıza en uygun olandır."
                </p>
                <p className="text-blue-800 font-medium mt-4">
                  — Dr. Barış Çin, Estetik Cerrahi Uzmanı
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Dudak dolgusu kalıcı mı?</h3>
                  <p className="text-gray-600">Hayır, ortalama 9–12 ay sürer.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Hangi marka en doğal sonucu verir?</h3>
                  <p className="text-gray-600">Juvederm Volbella ve Restylane Kysse.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. En uzun ömürlü dudak dolgusu hangisi?</h3>
                  <p className="text-gray-600">Belotero Intense ve Neauvia Intense Lips.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Dolgu erirse dudak küçülür mü?</h3>
                  <p className="text-gray-600">Hayır, dokular eski haline döner.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5. En ucuz dudak dolgusu hangisi?</h3>
                  <p className="text-gray-600">Stylage Special Lips ve Neauvia Intense Lips.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">6. Hangi marka FDA onaylı?</h3>
                  <p className="text-gray-600">Juvederm, Restylane ve Belotero FDA onaylıdır.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>FDA (2024) – "Approved Dermal Fillers for Lip Augmentation"</li>
                  <li>ISAPS Global Report (2024) – "Top Hyaluronic Acid Lip Fillers"</li>
                  <li>Harvard Health (2023) – "Choosing the Right Lip Filler"</li>
                  <li>Türk Estetik Tıp Derneği (TETD) – "Dudak Dolgusu Ürün Rehberi"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#dudakdolgu</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#juvederm</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#restylane</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#teosyal</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#belotero</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#estetik</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-rose-600 to-pink-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Dudak Dolgusu Marka Danışmanlığı</h3>
          <p className="mb-6">Uzman doktorumuzdan size en uygun dudak dolgusu markası hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Dudak dolgusu markaları hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-rose-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/dudak-dolgusu-teknikleri-dogal-simetrik-ve-estetik-sonuclar"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dudak Dolgusu Teknikleri</h4>
              <p className="text-gray-600 text-sm">Russian Lips, Paris Lips, Volume Lips gibi tekniklerin farkları ve uygulama süreci.</p>
            </a>
            <a
              href="/tr/blog/orijinal-dolgu-anlama-yontemleri-sahte-urunlerden-korunma"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Orijinal Dolguyu Anlama Yöntemleri</h4>
              <p className="text-gray-600 text-sm">Orijinal dolgu nasıl anlaşılır ve sahte ürünlerden nasıl korunulur?</p>
            </a>
            <a
              href="/tr/blog/dudaga-botoks-uygulamasi-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dudağa Botoks Uygulaması</h4>
              <p className="text-gray-600 text-sm">Dudak bölgesine botoks uygulaması nasıl yapılır? Etkileri ve uygulama süreci.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-rose-600 text-white rounded-full hover:bg-rose-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
